import * as ImSdk from '../../../imsdk/api';
import { TopicWithSummary } from '../../../imsdk/api/topic';
import { SystemCmd, SystemFriPass, SystemGroupIn, SystemGroupOut } from '../../constants/types';
import { getUid } from '../../store/token';
import { fetchUserInfo } from '../../net/database';
import { getDisplayName, isGroupTopic } from '../../utils/utils';
import { useAppNavigation } from '../navigation';
import { User } from '../../../imsdk/db/t_user';
import { GroupMember } from '../../../imsdk/db/t_group_member';
import { UserSchema } from '../../realm/contactSchema';
import {
	GetJoinGroupList,
	getGroupDetail,
	processGroupData,
	useChatDispatchOnly,
	useChatStateRefOnly,
} from '../../store/ChatStore';
import { CurTid } from './ChatScreen';
import { Platform } from 'react-native';
import globalEventBus from '../../utils/EventBus';

import {
	Str_ContactDeleted,
	Str_Cover,
	Str_CreateGroupChat,
	Str_ExitedGroupChat,
	Str_GroupChat,
	Str_Invited,
	Str_JoinGroupChat,
	Str_KickOutGroupChat,
	Str_Pulling,
	Str_ScanCodeAddGroup,
	Str_You,
	Str_YourFriendApproved,
} from '../../language/string_res';
import { useStore } from '../../store/UserStore';

/** 进入会话时先把列表里的未读角标清掉（不等 ChatList 里 SetTopicProps/topic_visit 链路） */
export function applyLocalReadToTopicSummaryInList(t: TopicWithSummary): TopicWithSummary {
	if (!t.summary?.unreadCnt || t.summary.unreadCnt <= 0) {
		return t;
	}
	return {
		...t,
		summary: { ...t.summary, unreadCnt: 0 },
	};
}

/** 当前正在看的会话：异步 GetTopicDetail / topic_visit 仍可能带回库里旧的 unreadCnt，避免把列表角标闪回 */
export function clearUnreadIfCurrentTopic(t: TopicWithSummary): TopicWithSummary {
	if (t.topic.tid !== CurTid.tid) {
		return t;
	}
	return applyLocalReadToTopicSummaryInList(t);
}

// 跳转 hook 内勿用 useChatStore() 全量订阅：否则会因切换会话导致挂载了本 hook 的列表页整表重渲染。

// 跳转到聊天详情
export function useNavigationToChatView() {
	const navigation = useAppNavigation();
	const chatDispatch = useChatDispatchOnly();
	const chatStateRef = useChatStateRefOnly();
	const { state, dispatch } = useStore();

	return async function (tid: string, isReplace: boolean = false): Promise<{ error: any }> {
		CurTid.tid = tid;
		let topic: TopicWithSummary | undefined;
		if (state && state.topicSummaryList.top) {
			const topics = state.topicSummaryList.top.filter((v) => v.topic.tid === tid);
			if (topics.length > 0) {
				topic = topics[0];
			}
		}
		if (state && state.topicSummaryList.normal) {
			const topics = state.topicSummaryList.normal.filter((v) => v.topic.tid === tid);
			if (topics.length > 0) {
				topic = topics[0];
			}
		}
		if (!topic) {
			const im = ImSdk.getInstance();
			const r = await im.GetTopicDetail(tid, true);
			topic = r.topic;
		}
		if (!topic) {
			return { error: '会话失败' };
		}
		let users = topic.users ? [...topic.users] : [];
		if (!isGroupTopic(tid) && state?.user) {
			if (!users.map((v) => v.uid).includes(state.user.uid)) {
				users.push(state.user as User);
			}
		}
		const next: TopicWithSummary = { ...topic, users };
		const cleared = applyLocalReadToTopicSummaryInList(next);
		dispatch({ type: 'UPSERT_TOPIC_SUMMARY', payload: [cleared] });
		chatDispatch({ type: 'CHAT_SWITCH_TOPIC', payload: cleared });

		if (isReplace) {
			navigation.replace('ChatList');
		} else {
			navigation.navigate('ChatList');
		}
		setTimeout(() => GetJoinGroupList(chatStateRef.current, chatDispatch, tid), 0);
		return { error: null };
	};
}
// 跳转到聊天详情
export function useNavigationToChatView2() {
	const navigation = useAppNavigation();
	const chatDispatch = useChatDispatchOnly();
	const chatStateRef = useChatStateRefOnly();
	const { state, dispatch } = useStore();

	return async function (topicSummary: TopicWithSummary, isReplace: boolean = false): Promise<{ error: any }> {
		const tid = topicSummary.topic.tid;
		CurTid.tid = tid;
		let next: TopicWithSummary;
		if (!isGroupTopic(tid)) {
			const users = [...(topicSummary.users || [])];
			if (state?.user && !users.map((v) => v.uid).includes(state.user.uid)) {
				users.push(state.user as User);
			}
			next = { ...topicSummary, users };
		} else {
			next = { ...topicSummary };
		}
		const cleared = applyLocalReadToTopicSummaryInList(next);
		dispatch({ type: 'UPSERT_TOPIC_SUMMARY', payload: [cleared] });
		chatDispatch({ type: 'CHAT_SWITCH_TOPIC', payload: cleared });
		const titleName = cleared.topic.remark || cleared.topic.name || Str_GroupChat;

		if (Platform.OS === 'web') {
			globalEventBus.emit('ShowWebDetail', {
				screenName: 'ChatList',
				params: { topic: cleared, titleName },
			});
		} else {
			if (isReplace) {
				navigation.replace('ChatList', { titleName });
			} else {
				navigation.navigate('ChatList', { titleName });
			}
		}
		setTimeout(() => {
			if (isGroupTopic(tid)) {
				processGroupData(
					{ group: cleared.group, members: (cleared.users || []) as GroupMember[] },
					chatDispatch,
				);
			} else if (!cleared.users?.length) {
				getGroupDetail(tid, chatDispatch);
			}
			GetJoinGroupList(chatStateRef.current, chatDispatch, tid);
		}, 0);
		return { error: null };
	};
}

/**
 * 展示系统消息
 * @param obj
 * @returns
 */
export async function showSystemMsg(obj: SystemGroupIn & SystemGroupOut & SystemFriPass, users?: (User | GroupMember)[]) {
	const SystemCmdTip = {
		[SystemCmd.GROUP_CREATE]: `s1 ${Str_Pulling} s2 ${Str_CreateGroupChat}`,
		[SystemCmd.GROUP_JOIN]: `s1 ${Str_Invited} s2 ${Str_JoinGroupChat}`,
		[SystemCmd.GROUP_JOIN_KEFU]: `s1、s2 ${Str_JoinGroupChat}`,
		[SystemCmd.GROUP_QUIT]: `s1 ${Str_ExitedGroupChat}`,
		[SystemCmd.GROUP_KICK]: `s2 ${Str_Cover} s1 ${Str_KickOutGroupChat}`,
		[SystemCmd.FRI_REQ_PASSS]: `s1 ${Str_YourFriendApproved}`,
	};

	let s1Name = await getSystemTipDisplayName(obj.uid, users);

	if (obj.cmd === SystemCmd.GROUP_CREATE || obj.cmd === SystemCmd.GROUP_JOIN || obj.cmd === SystemCmd.GROUP_JOIN_KEFU) {
		if (obj.joinUids) {
			const s2Names = await joinUserName(obj, users);
			if (obj.cmd === SystemCmd.GROUP_JOIN && s1Name === s2Names) {
				return `${s1Name} ${Str_ScanCodeAddGroup}`;
			}
			return SystemCmdTip[obj.cmd].replace('s1', s1Name).replace('s2', s2Names);
		}
	} else if (obj.cmd === SystemCmd.GROUP_KICK || obj.cmd === SystemCmd.GROUP_QUIT) {
		if (obj.joinUids) {
			const s2Names = await joinUserName(obj, users);
			return SystemCmdTip[obj.cmd].replace('s1', s1Name).replace('s2', s2Names);
		}
	} else if (obj.cmd === SystemCmd.FRI_REQ_PASSS) {
		return SystemCmdTip[obj.cmd].replace('s1', s1Name);
	}
	return obj.show;
}

const joinUserName = async (obj: SystemGroupIn & SystemGroupOut & SystemFriPass, users?: (User | GroupMember)[]) => {
	if (!obj.joinUids || obj.joinUids.length === 0) {
		return '';
	}

	const inUsers: (User | GroupMember | UserSchema)[] = [];
	let outUsers: number[] = [];
	if (obj.joinUids && obj.joinUids.length > 0) {
		if (users) {
			obj.joinUids.forEach((v) => {
				const filter = users.filter((item) => item.uid === v);
				if (filter.length > 0) {
					inUsers.push(filter[0]);
				} else {
					outUsers.push(v);
				}
			});
		} else {
			outUsers = obj.joinUids;
		}
	}
	if (outUsers.length > 0) {
		const r = await ImSdk.getInstance().GetUsersRemote(outUsers);
		if (r.users && r.users.length > 0) {
			inUsers.push(...r.users);
		}
	}
	return inUsers.map((user) => (user?.uid === getUid() ? Str_You : getDisplayName(user))).join('、');
}

async function getSystemTipDisplayName(uid: number, users?: (User | GroupMember)[]) {
	let s1Name = '';
	if (uid === getUid()) {
		s1Name = Str_You;
	} else {
		let user;
		if (users && users.length > 0) {
			const index = users.findIndex((v) => v.uid === uid);
			if (index >= 0) {
				user = users[index];
			}
		}
		if (user) {
			s1Name = getDisplayName(user);
		} else {
			const r = await ImSdk.getInstance().GetUser(uid, true);
			if (r.user) {
				s1Name = getDisplayName(r.user);
			}
		}
	}
	return s1Name;
}

export function getUserName(user?: User | GroupMember | UserSchema): string {
	if (!user) {
		return '';
	}
	if (user.status === 'deleted') {
		return Str_ContactDeleted;
	}
	if (user.remark && user.remark.length > 0) {
		return user.remark;
	} else if (user.nick && user.nick.length > 0) {
		return user.nick;
	} else if (user.name && user.name.length > 0) {
		return user.name;
	} else if (user.email && user.email.length > 0) {
		return user.email;
	} else if (user.telno && user.telno.length > 0) {
		return user.telno;
	} else {
		return '';
	}
}

export const navigateToUserInfo = () => {
	const navigation = useAppNavigation();
	// 动态导入 globalEventBus 避免循环依赖
	const globalEventBus = require('../../utils/EventBus').default;

	function handleToUserInfo(user: any) {
		// 使用事件总线显示弹窗，而不是导航
		globalEventBus.emit('ShowContactInfo', user);
	}

	return { handleToUserInfo };
};
